import React from 'react';
import { observer } from 'mobx-react';

import dayjs from 'dayjs';

import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { NotificationType } from 'utils/api/modules/user/user.types';
import { useStore } from 'utils/hooks/store';
import Avatar from 'components/Avatar/Avatar';
import Chip from 'components/Chip/Chip';
import Notification from 'containers/Dashboard/components/Notification';
import {
  getCategoryForNotification,
  getLinkForNotification,
  isDueTodayNotification,
  CATEGORY_CONFIG,
  NotificationCategory,
} from 'containers/KnockNotifications/constants';

import { ItemContainer, UnreadDot, ContentWrapper, MetaRow, TimeText, IconCircle } from './styles';

const KnockNotificationItem: React.FunctionComponent<{ item: NotificationType }> = ({
  item,
}): React.ReactElement | null => {
  const { dualInbox } = useStore();
  const { activeCategory } = dualInbox;
  const category = getCategoryForNotification(item.target);
  if (category === undefined) return null;
  const config = CATEGORY_CONFIG[category];
  const linkTo = getLinkForNotification(item);
  // The category is already established by the nav in a per-module view, so the
  // tag only earns its place in the combined "All" view.
  const showCategory = activeCategory === NotificationCategory.ALL && Boolean(config.label);

  // The whole card is the click target (Fitts) and is itself the anchor, so the
  // browser handles navigation natively (new-tab affordances, keyboard). The click
  // only needs to mark the notification read. disableLink drops the now-redundant
  // anchor inside the message so the card holds a single navigation target.
  const handleMarkRead = (): void => {
    if (!item.dismissed) {
      dualInbox.markNotificationAsRead(item.uuid);
    }
  };

  return (
    <ItemContainer to={linkTo} data-testid="knock-notification-item" onClick={handleMarkRead}>
      <UnreadDot $visible={!item.dismissed} />
      {isDueTodayNotification(item.target) ? (
        <IconCircle>
          <AccessTimeIcon fontSize="small" aria-hidden />
        </IconCircle>
      ) : (
        <Avatar name={item.creator?.name} empty={!item.creator?.name} />
      )}
      <ContentWrapper>
        <Notification notification={item} disableLink />
        <MetaRow>
          <TimeText>{dayjs(item.created).fromNow()}</TimeText>
          {showCategory && <Chip label={config.label} />}
        </MetaRow>
      </ContentWrapper>
    </ItemContainer>
  );
};

export default observer(KnockNotificationItem);
